import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, openWhatsApp } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

const filters = [
  { label: "All", value: "all" },
  { label: "Scented", value: "scented" },
  { label: "Floral", value: "floral" },
  { label: "Figurines", value: "plaster" },
  { label: "DIY Kits", value: "kits" },
  { label: "Decor", value: "decor" },
];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, []);

  const visible = active === "all" ? products : products.filter((p) => p.category === active);

  const order = (p) => {
    toast.success("Opening WhatsApp…", { description: `Enquiry for ${p.name}` });
    openWhatsApp(`Hi Ave_luxe_candles! I'm interested in "${p.name}" (${p.price}). Is it available?`);
  };

  return (
    <section id="collection" className="max-w-7xl mx-auto px-6 py-28 sm:py-36" data-testid="collection-section">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#C4A47C]" />
          <span className="label text-[#C4A47C]">The Collection</span>
        </div>
        <h2 className="font-serif font-light text-4xl sm:text-6xl tracking-tighter text-[#F5F5F0] max-w-2xl leading-[1.02]">
          Objects made to be lit, loved & gifted.
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-12 flex flex-wrap gap-3" data-testid="catalog-filters">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setActive(f.value)}
              data-testid={`filter-${f.value}`}
              className={`rounded-full px-5 py-2.5 text-sm transition-colors border ${
                active === f.value
                  ? "bg-[#C4A47C] border-[#C4A47C] text-[#0C0A09]"
                  : "border-[#292524] text-[#F5F5F0]/80 hover:border-[#C4A47C]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Reveal>

      {loading ? (
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-[420px] rounded-sm border border-[#292524] bg-[#1C1917] animate-pulse" />
          ))}
        </div>
      ) : (
        <motion.div layout className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                className="group border border-[#292524] rounded-sm overflow-hidden bg-[#1C1917]/40"
                data-testid={`product-card-${p.id}`}
              >
                <div className="img-zoom relative aspect-[4/5]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  {p.featured && (
                    <span className="absolute top-4 left-4 label text-[#0C0A09] bg-[#C4A47C] px-3 py-1 rounded-full">
                      Bestseller
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-2xl text-[#F5F5F0] tracking-tight leading-tight">{p.name}</h3>
                    <span className="font-serif text-xl text-[#C4A47C] whitespace-nowrap">{p.price}</span>
                  </div>
                  <p className="mt-3 text-sm text-[#A8A29E] font-light leading-relaxed line-clamp-3">
                    {p.description}
                  </p>
                  <button
                    onClick={() => order(p)}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#292524] group-hover:border-[#C4A47C] group-hover:bg-[#C4A47C] group-hover:text-[#0C0A09] text-[#F5F5F0] px-5 py-3 text-sm font-medium transition-colors"
                    data-testid={`order-btn-${p.id}`}
                  >
                    <MessageCircle size={16} strokeWidth={1.5} />
                    Enquire on WhatsApp
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {!loading && visible.length === 0 && (
        <p className="mt-16 text-[#A8A29E] font-light" data-testid="no-products">
          No products in this category yet.
        </p>
      )}
    </section>
  );
}
